import React, { useRef, useState } from "react";

const inputClassName =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/25";

const COOLDOWN_MS = 45_000;
const MIN_MESSAGE_LEN = 12;

function isValidEmail(value) {
  const s = value.trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  /** Honeypot — must stay empty; bots often fill "website"/"company" fields. */
  const [websiteHoneypot, setWebsiteHoneypot] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const lastSubmitAtRef = useRef(0);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    setFieldErrors({});

    if (websiteHoneypot.trim() !== "") {
      return;
    }

    const now = Date.now();
    if (lastSubmitAtRef.current > 0 && now - lastSubmitAtRef.current < COOLDOWN_MS) {
      setFormError("Please wait a moment before sending again.");
      return;
    }

    const n = name.trim();
    const em = email.trim();
    const ph = phone.trim();
    const msg = message.trim();

    const nextFieldErrors = {};
    if (n.length < 2) {
      nextFieldErrors.name = "Please enter your name.";
    }
    if (!isValidEmail(em)) {
      nextFieldErrors.email = "Please enter a valid email address.";
    }
    if (msg.length < MIN_MESSAGE_LEN) {
      nextFieldErrors.message = `Please add a bit more detail (at least ${MIN_MESSAGE_LEN} characters).`;
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    lastSubmitAtRef.current = now;

    const bodyLines = [`From: ${n}`, `Email: ${em}`];
    if (ph) bodyLines.push(`Phone: ${ph}`);
    bodyLines.push("", msg);
    const subject = `Website inquiry from ${n}`;
    const mailto = `mailto:info@markbeattie.ca?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      bodyLines.join("\n")
    )}`;
    window.location.href = mailto;
  };

  return (
    <div className="bg-secondary">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-7 md:py-9">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Contact</h1>
          <div className="mt-4 h-px w-12 bg-slate-300/90" aria-hidden="true" />
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Tell me a bit about your property, project, or goals — I&apos;ll get back to you.
          </p>
        </div>
      </header>

      <section className="py-10 md:py-12">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:items-start md:gap-10 lg:gap-10">
            <div className="max-w-md space-y-6">
              <p className="text-slate-700 leading-relaxed">
                I work with homeowners and investors looking to make better decisions early — whether buying,
                planning, or building.
              </p>

              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Email</h2>
                <a
                  href="mailto:info@markbeattie.ca"
                  className="mt-1 inline-block text-base font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 transition hover:text-slate-700 hover:decoration-slate-400"
                >
                  info@markbeattie.ca
                </a>
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-900">Typical inquiries include:</p>
                <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-slate-700 marker:text-slate-400">
                  <li>Residential buying and selling</li>
                  <li>Renovation and design planning</li>
                  <li>Construction and project guidance</li>
                </ul>
              </div>
            </div>

            <div className="w-full max-w-lg md:justify-self-end">
              <form className="relative space-y-4" onSubmit={handleSubmit} noValidate>
                {/* Honeypot: off-screen, not display:none, so simple bots may still fill it */}
                <div
                  className="pointer-events-none absolute left-[-10000px] top-auto h-0 w-0 overflow-hidden opacity-0"
                  aria-hidden="true"
                >
                  <label htmlFor="contact-website">Company website</label>
                  <input
                    id="contact-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={websiteHoneypot}
                    onChange={(ev) => setWebsiteHoneypot(ev.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-slate-700">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(ev) => setName(ev.target.value)}
                    className={`mt-1.5 ${inputClassName}`}
                    aria-invalid={fieldErrors.name ? "true" : undefined}
                    aria-describedby={fieldErrors.name ? "contact-name-error" : undefined}
                  />
                  {fieldErrors.name ? (
                    <p id="contact-name-error" className="mt-1.5 text-sm text-red-700" role="alert">
                      {fieldErrors.name}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-slate-700">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    className={`mt-1.5 ${inputClassName}`}
                    aria-invalid={fieldErrors.email ? "true" : undefined}
                    aria-describedby={fieldErrors.email ? "contact-email-error" : undefined}
                  />
                  {fieldErrors.email ? (
                    <p id="contact-email-error" className="mt-1.5 text-sm text-red-700" role="alert">
                      {fieldErrors.email}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-slate-700">
                    Phone <span className="font-normal text-slate-500">(optional)</span>
                  </label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(ev) => setPhone(ev.target.value)}
                    className={`mt-1.5 ${inputClassName}`}
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-slate-700">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    value={message}
                    onChange={(ev) => setMessage(ev.target.value)}
                    className={`mt-1.5 min-h-[120px] resize-y ${inputClassName}`}
                    aria-invalid={fieldErrors.message ? "true" : undefined}
                    aria-describedby={fieldErrors.message ? "contact-message-error" : undefined}
                  />
                  {fieldErrors.message ? (
                    <p id="contact-message-error" className="mt-1.5 text-sm text-red-700" role="alert">
                      {fieldErrors.message}
                    </p>
                  ) : null}
                </div>

                {formError ? (
                  <p className="text-sm text-red-800" role="alert">
                    {formError}
                  </p>
                ) : null}

                <button
                  type="submit"
                  className="inline-flex min-h-[42px] w-full items-center justify-center rounded-md bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-px hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 sm:w-auto"
                >
                  Send message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
